import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { DARK_COLORS, BREAKPOINTS } from '@/constants';

import SelectControl from '@/components/Controls/SelectControl';
import Space from '@/components/Space';

import BaseItem from './Item';
import { LabeledColumn, LabeledRow } from './Grid.shared';

function JustifyAlign() {
  const [justifyContent, setJustifyContent] = React.useState('start');
  const [justifyItems, setJustifyItems] = React.useState('stretch');
  const [alignContent, setAlignContent] = React.useState('start');
  const [alignItems, setAlignItems] = React.useState('stretch');

  const gridTemplateColumns = '100px 100px';
  const gridTemplateRows = '100px 100px';

  return (
    <Wrapper>
      <Row>
        <ControlsWrapper>
          <SelectControl
            id="justifyContent"
            label="justify-content"
            value={justifyContent}
            setValue={setJustifyContent}
            options={[
              'start',
              'center',
              'end',
              'space-between',
              'space-around',
              'space-evenly',
            ]}
          />
          <SelectControl
            id="justifyItems"
            label={'justify-items'}
            value={justifyItems}
            setValue={setJustifyItems}
            options={['stretch', 'start', 'center', 'end']}
          />
          <SelectControl
            isNew
            id="alignContent"
            label="align-content"
            value={alignContent}
            setValue={setAlignContent}
            options={[
              'start',
              'center',
              'end',
              'space-between',
              'space-around',
              'space-evenly',
            ]}
          />
          <SelectControl
            isNew
            id="alignItems"
            label="align-items"
            value={alignItems}
            setValue={setAlignItems}
            options={['stretch', 'start', 'center', 'end']}
          />
        </ControlsWrapper>
        <DemoWrapper layout layoutRoot>
          <BackLayer>
            {/* Background (with bg) */}
            <RowStructure style={{ alignContent }}>
              <LabeledRow
                layout={true}
                style={{ gridColumn: '1 / 3', gridRow: 1 }}
              >
                R1
              </LabeledRow>
              <LabeledRow
                layout={true}
                style={{ gridColumn: '1 / 3', gridRow: 2 }}
              >
                R2
              </LabeledRow>
            </RowStructure>
            <ColumnStructure
              style={{
                justifyContent,
              }}
            >
              <LabeledColumn
                layout={true}
                style={{ gridColumn: 1, gridRow: '1 / 3' }}
              >
                C1
              </LabeledColumn>
              <LabeledColumn
                layout={true}
                style={{ gridColumn: 2, gridRow: '1 / 3' }}
              >
                C2
              </LabeledColumn>
            </ColumnStructure>

            {/* Foreground (no bg) */}
            <RowStructure style={{ alignContent }}>
              <LabeledRow
                layout={true}
                style={{
                  gridColumn: '1 / 3',
                  gridRow: 1,
                  background: 'transparent',
                }}
              >
                R1
              </LabeledRow>
              <LabeledRow
                layout={true}
                style={{
                  gridColumn: '1 / 3',
                  gridRow: 2,
                  background: 'transparent',
                }}
              >
                R2
              </LabeledRow>
            </RowStructure>
            <ColumnStructure
              style={{
                justifyContent,
              }}
            >
              <LabeledColumn
                layout={true}
                style={{
                  gridColumn: 1,
                  gridRow: '1 / 3',
                  background: 'transparent',
                }}
              >
                C1
              </LabeledColumn>
              <LabeledColumn
                layout={true}
                style={{
                  gridColumn: 2,
                  gridRow: '1 / 3',
                  background: 'transparent',
                }}
              >
                C2
              </LabeledColumn>
            </ColumnStructure>
          </BackLayer>
          <Layer>
            <ChildWrapper
              style={{
                gridTemplateColumns,
                gridTemplateRows,
                justifyContent,
                justifyItems,
                alignContent,
                alignItems,
              }}
            >
              <Item>One</Item>
              <Item>Two</Item>
              <Item>Three</Item>
              <Item>Four</Item>
            </ChildWrapper>
          </Layer>
        </DemoWrapper>
      </Row>
    </Wrapper>
  );
}

const Wrapper = styled(Space)`
  --color-background: ${DARK_COLORS['--syntax-bg']};
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-bottom: 48px;
  padding: var(--viewport-padding);
  background: var(--color-background);
  border-radius: 0px;
  outline: 1px solid var(--color-content-outline);

  @media ${BREAKPOINTS.mdAndLarger} {
    border-radius: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 80px;

  @media (max-width: 41.625rem) {
    flex-direction: column;
    align-items: center;
    gap: 4rem;
  }
`;

const ControlsWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 130px;

  @media (max-width: 41.625rem) {
    width: 100%;
  }
`;

const DemoWrapper = styled(motion.div)`
  flex: 1.2;
  position: relative;
  min-height: 317px;
  align-self: flex-end;

  @media (max-width: 41.625rem) {
    width: 100%;
    max-width: calc(100% - 64px);
    align-self: center;
  }
`;

const Layer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const BackLayer = styled(Layer)`
  display: grid;
  padding: 2px;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
  background: repeating-linear-gradient(
    45deg,
    hsl(210deg 40% 80% / 0.06) 0px,
    hsl(210deg 40% 80% / 0.06) 10px,
    hsl(210deg 40% 80% / 0) 10px,
    hsl(210deg 40% 80% / 0) 20px
  );
`;

const ColumnStructure = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-columns: 100px 100px;
  gap: 2px;
  height: 100%;
`;
const RowStructure = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-rows: 100px 100px;
  gap: 2px;
  width: 100%;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 2px;
  padding: 2px;
`;

const Item = styled(BaseItem)`
  margin: 3px;
  font-size: 0.875rem;
`;

export default JustifyAlign;
